/**
 * Validates and sanitizes a URL path to prevent open redirect vulnerabilities.
 * Only allows relative paths that start with '/' and do not contain protocol schemes.
 * 
 * @param {string} path - The path to validate
 * @returns {string|null} - The sanitized path or null if invalid
 */
export function sanitizeInternalPath(path) {
  if (!path || typeof path !== 'string') {
    return null;
  }

  // Trim whitespace
  const trimmedPath = path.trim();

  // Only allow paths starting with '/'
  if (!trimmedPath.startsWith('/')) {
    return null;
  }

  // Prevent double slashes at the start (which could bypass origin in protocol-relative URLs)
  if (trimmedPath.startsWith('//')) {
    return null;
  }

  // Check for protocol schemes in original path (http://, https://, javascript:, data:, etc.)
  const hasProtocol = /^[a-zA-Z][a-zA-Z0-9+.-]*:/i.test(trimmedPath);
  if (hasProtocol) {
    return null;
  }

  // Check for protocol schemes in decoded path (handles URL-encoded attacks)
  try {
    const decodedPath = decodeURIComponent(trimmedPath);
    
    // Check if decoded path starts with // (protocol-relative URL)
    if (decodedPath.startsWith('//')) {
      return null;
    }
    
    // Check for protocol schemes in decoded path
    const hasDecodedProtocol = /^[a-zA-Z][a-zA-Z0-9+.-]*:/i.test(decodedPath);
    if (hasDecodedProtocol) {
      return null;
    }
  } catch (e) {
    // If decoding fails, reject the path
    return null;
  }

  return trimmedPath;
}

/**
 * Creates a safe internal URL by combining origin with a sanitized path.
 * 
 * @param {string} origin - The origin (e.g., window.location.origin)
 * @param {string} path - The path to append
 * @returns {string|null} - The safe URL or null if path is invalid
 */
export function createSafeInternalUrl(origin, path) {
  const sanitizedPath = sanitizeInternalPath(path);
  if (!sanitizedPath) {
    return null;
  }

  return origin + sanitizedPath;
}
